import { RequisitionRow } from "../../recoil/types";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { UpdateArchiveStatus } from "./Services/GridService";
import { Toast } from "./useToast";

export type ArchiveRowData = {
  reqID: string;
  name: string;
  physician: string;
  reported: string;
  dob: string;
  collected: string;
};

export const createRowDataForUndo = (
  row: RequisitionRow | ArchiveRowData | undefined,
): ArchiveRowData | undefined => {
  if (!row) return undefined;
  return {
    reqID: row.reqID,
    name: row.name,
    physician: row.physician,
    reported: row.reported,
    dob: row.dob,
    collected: row.collected,
  };
};

interface UseArchiveActionsParams {
  token?: string;
  username?: string;
  setLoggedOut: (inactivity: boolean) => void;
  toast: Toast;
  refreshVisibleRequisitionData: () => Promise<void>;
  completedRows: RequisitionRow[];
  archivedRows: RequisitionRow[];
}

export interface ArchiveActions {
  handleArchive: (sfid: string, rowData?: ArchiveRowData) => Promise<void>;
  handleUnarchive: (sfid: string, rowData?: ArchiveRowData) => Promise<void>;
}

export const useArchiveActions = ({
  token,
  username,
  setLoggedOut,
  toast,
  refreshVisibleRequisitionData,
  completedRows,
  archivedRows,
}: UseArchiveActionsParams): ArchiveActions => {
  const handleArchive = async (
    sfid: string,
    rowData?: ArchiveRowData,
  ): Promise<void> => {
    if (!token) return;
    const row = rowData || completedRows.find((r) => r.sfID === sfid);
    if (!row) return;
    const rowDataForUndo = createRowDataForUndo(row);
    try {
      const response = await UpdateArchiveStatus(
        token,
        sfid,
        "archived",
        setLoggedOut,
      );
      if (response.ok) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.OrderArchived,
          token,
          username,
          {
            sfid,
          },
        );
        toast.showToast("Order archived successfully", {
          undoAction: () => handleUnarchive(sfid, rowDataForUndo),
        });
        await refreshVisibleRequisitionData();
      } else {
        toast.showError("Failed to archive order");
      }
    } catch {
      toast.showError("Failed to archive order");
    }
  };

  const handleUnarchive = async (
    sfid: string,
    rowData?: ArchiveRowData,
  ): Promise<void> => {
    if (!token) return;
    const row = rowData || archivedRows.find((r) => r.sfID === sfid);
    const rowDataForUndo = createRowDataForUndo(row);
    try {
      const response = await UpdateArchiveStatus(
        token,
        sfid,
        "completed",
        setLoggedOut,
      );
      if (response.ok) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.OrderUnarchived,
          token,
          username,
          { sfid },
        );
        toast.showToast(
          "Order un-archived successfully",
          rowDataForUndo
            ? { undoAction: () => handleArchive(sfid, rowDataForUndo) }
            : undefined,
        );
        await refreshVisibleRequisitionData();
      } else {
        toast.showError("Failed to un-archive order");
      }
    } catch {
      toast.showError("Failed to un-archive order");
    }
  };

  return { handleArchive, handleUnarchive };
};
